/**
 * EmployerWorkflowPage.jsx — RC EMPLOYER.PUBLIC-EXPERIENCE.PHASE-A
 *
 * In-app "How it works" — operational guidance for employer users.
 *
 * This is the IN-APP counterpart of the public marketing
 * /how-it-works page. The public page positions; this page
 * orients an authenticated employer user inside their workspace:
 * what each surface is for, what the workflow looks like, and how
 * governance is preserved.
 *
 * Read-only. No backend call. Pure FE static content. Mirrors the
 * structural shape of EmployerPermissionsPage.jsx (the existing
 * Help surface).
 *
 * Strict positioning:
 *   • NDORFlow is NOT an ATS. It augments hiring decisions; it
 *     does not replace your ATS.
 *   • Evaluations are reasoning support — not auto-rejection.
 *   • Signals are mentioned LIGHTLY only — they are not production-
 *     mature yet (per phase brief).
 *   • Tone: institutional, calm, governance-oriented, non-hype.
 */
import { Link } from 'react-router-dom';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '@/components/ui/card';
import {
  ArrowLeft, Workflow, Briefcase, Users, ClipboardCheck,
  GitBranch, FileCheck2, Scale, ShieldCheck, BookOpen, Inbox,
} from 'lucide-react';


// ─────────────────────────────────────────────────────────────────
// Section 1 — Hiring workflow (6 stages mirroring the public page,
// but framed operationally: "what you do" rather than "what we are").
// Wording is intentionally short — this is a primer, not a manual.
// ─────────────────────────────────────────────────────────────────
const WORKFLOW_STEPS = [
  {
    n: 1,
    icon: Briefcase,
    title: 'Create a role',
    body: (
      <>
        Define requirements, expectations, and dealbreakers. NDORFlow
        converts them into a structured evaluation framework that
        travels with the role.
        <span className="block mt-2 text-xs text-ndor-ink-muted">
          Surface · <Link to="/employer/roles" className="underline">Roles</Link>
        </span>
      </>
    ),
  },
  {
    n: 2,
    icon: GitBranch,
    title: 'Define the evaluation framework',
    body: (
      <>
        Calibrate the criteria each candidate will be assessed against
        for this specific role — not a generic taxonomy. The framework
        is preserved alongside every assessment.
        <span className="block mt-2 text-xs text-ndor-ink-muted">
          Surface · Role wizard (inside Roles)
        </span>
      </>
    ),
  },
  {
    n: 3,
    icon: Users,
    title: 'Upload Candidate Dossiers',
    body: (
      <>
        Direct applicants, uploaded CVs and imported candidates are
        normalised into Candidate Dossiers — one structured artefact
        per identity, normalised regardless of how they arrived.
        <span className="block mt-2 text-xs text-ndor-ink-muted">
          Surface · <Link to="/employer/candidates" className="underline">Candidates</Link>
        </span>
      </>
    ),
  },
  {
    n: 4,
    icon: ClipboardCheck,
    title: 'Review the dossier assessment',
    body: (
      <>
        Each Candidate Dossier carries a reviewable assessment against
        the role’s framework — strengths, gaps, evidence quality,
        reasoning consistency. You see the working, not just a verdict.
        <span className="block mt-2 text-xs text-ndor-ink-muted">
          Surface · <Link to="/employer/reviews" className="underline">Reviews</Link>
        </span>
      </>
    ),
  },
  {
    n: 5,
    icon: Scale,
    title: 'Shortlist dossiers',
    body: (
      <>
        Compare Candidate Dossiers side-by-side against the same
        framework. Reduce inconsistency between reviewers. Preserve
        the reasoning trail behind every advance and rejection.
      </>
    ),
  },
  {
    n: 6,
    icon: FileCheck2,
    title: 'Export the Governance Pack',
    body: (
      <>
        The Dossier’s assessment, the reasoning trail, the verdict
        chain, and the operator identity are frozen into an immutable
        Governance Pack — PDF + DOCX, SHA-anchored. The decision record
        outlives the role.
      </>
    ),
  },
];


// ─────────────────────────────────────────────────────────────────
// Section 2 — Understanding NDORFlow concepts.
// Light touch on Signals per phase brief — listed but not lead.
// ─────────────────────────────────────────────────────────────────
const CONCEPTS = [
  {
    icon: ClipboardCheck,
    title: 'Dossier evaluations',
    body: (
      <>
        A structured assessment of one Candidate Dossier against one
        role’s evaluation framework. Includes evidence, reasoning and
        a recommendation — never a hidden score.
      </>
    ),
  },
  {
    icon: FileCheck2,
    title: 'Governance Packs',
    body: (
      <>
        An immutable, SHA-anchored decision record exported as PDF +
        DOCX. Cover, governance summary, interpretation, evidence
        chain, verdict, operator identity. The pack is the decision
        record.
      </>
    ),
  },
  {
    icon: Briefcase,
    title: 'Role activations',
    body: (
      <>
        Each open role you’re actively hiring against consumes one
        active-role slot from your plan. Closing or archiving a role
        returns its slot for reuse.
      </>
    ),
  },
  {
    icon: BookOpen,
    title: 'Reasoning support',
    body: (
      <>
        NDORFlow surfaces evidence and reasoning. Reviewers and hiring
        managers still make the call — the platform makes the call
        defensible.
      </>
    ),
  },
  {
    icon: Inbox,
    title: 'Signals',
    // RC 1.62 Role Lifecycle Gate Hardening (2026-05-30) — Channel-2
    // copy now reflects entitlement-gated reality, not roadmap.
    body: (
      <>
        Anonymous candidate matches and consent-based profile reveal —
        built and live, available for enabled workspaces. Evidence-led
        decisions today happen in Roles via imported candidates and
        evaluations.
      </>
    ),
  },
];


export default function EmployerWorkflowPage() {
  return (
    <div
      className="p-4 sm:p-6 max-w-5xl mx-auto space-y-8"
      data-testid="employer-workflow-page"
    >
      {/* ── Header ───────────────────────────────────────────── */}
      <header className="space-y-2">
        <Link
          to="/employer"
          className="inline-flex items-center text-xs text-ndor-ink-muted hover:text-ndor-ink"
          data-testid="employer-workflow-back"
        >
          <ArrowLeft className="h-3 w-3 mr-1" /> Back to Dashboard
        </Link>
        <h1
          className="text-2xl font-semibold tracking-tight text-ndor-ink flex items-center gap-2"
          data-testid="employer-workflow-heading"
        >
          <Workflow className="h-5 w-5 text-ndor-ink-muted" />
          How NDORFlow works
        </h1>
        <p className="text-sm text-ndor-ink-secondary max-w-3xl">
          NDORFlow is a structured hiring decision-support layer — not
          an ATS, not auto-apply, not keyword ranking. This page
          orients you to the workflow each role moves through and the
          concepts behind it.
        </p>
      </header>

      {/* ── Section 1 — Workflow ─────────────────────────────── */}
      <section
        className="space-y-4"
        data-testid="employer-workflow-section-pipeline"
      >
        <div>
          <h2 className="text-lg font-semibold text-ndor-ink">
            Your hiring workflow
          </h2>
          <p className="text-xs text-ndor-ink-muted mt-1">
            Six stages. Each one builds the evidence trail behind the
            next. Decisions stay with your people; the platform makes
            them consistent.
          </p>
        </div>
        <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {WORKFLOW_STEPS.map(({ n, icon: Icon, title, body }) => (
            <li key={n}>
              <Card className="h-full border-ndor-edge bg-ndor-surface-alt/40">
                <CardHeader className="pb-2">
                  <div className="flex items-baseline gap-2">
                    <span className="font-mono text-[11px] tracking-wider text-ndor-ink-muted">
                      {String(n).padStart(2, '0')}
                    </span>
                    <Icon className="h-3.5 w-3.5 text-ndor-ink-muted" />
                  </div>
                  <CardTitle className="text-sm font-medium text-ndor-ink">
                    {title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-xs text-ndor-ink-secondary leading-relaxed">
                  {body}
                </CardContent>
              </Card>
            </li>
          ))}
        </ol>
      </section>

      {/* ── Section 2 — Understanding NDORFlow ───────────────── */}
      <section
        className="space-y-4"
        data-testid="employer-workflow-section-concepts"
      >
        <div>
          <h2 className="text-lg font-semibold text-ndor-ink">
            Understanding NDORFlow
          </h2>
          <p className="text-xs text-ndor-ink-muted mt-1">
            A short glossary of the concepts you will see in the
            workspace.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {CONCEPTS.map(({ icon: Icon, title, body }) => (
            <Card
              key={title}
              className="border-ndor-edge bg-ndor-surface-alt/40"
            >
              <CardHeader className="pb-2 flex flex-row items-center gap-2 space-y-0">
                <Icon className="h-4 w-4 text-ndor-ink-muted" />
                <CardTitle className="text-sm font-medium text-ndor-ink">
                  {title}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs text-ndor-ink-secondary leading-relaxed">
                {body}
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* ── Section 3 — Governance & Consistency ─────────────── */}
      <section
        className="space-y-4"
        data-testid="employer-workflow-section-governance"
      >
        <div>
          <h2 className="text-lg font-semibold text-ndor-ink">
            Governance &amp; consistency
          </h2>
          <p className="text-xs text-ndor-ink-muted mt-1">
            What stays preserved when you hire through NDORFlow.
          </p>
        </div>
        <Card className="border-ndor-edge bg-ndor-surface-alt/40">
          <CardHeader className="pb-2 flex flex-row items-center gap-2 space-y-0">
            <ShieldCheck className="h-4 w-4 text-ndor-ink-muted" />
            <CardTitle className="text-sm font-medium text-ndor-ink">
              A defensible record of every hire
            </CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-ndor-ink-secondary leading-relaxed space-y-3">
            <ul className="space-y-1.5 list-disc list-inside marker:text-ndor-ink-muted">
              <li>
                <span className="text-ndor-ink">Structured review.</span>{' '}
                Every candidate is assessed against the same framework
                for the role they applied to.
              </li>
              <li>
                <span className="text-ndor-ink">Decision consistency.</span>{' '}
                The framework reduces reviewer variance without removing
                reviewer judgement.
              </li>
              <li>
                <span className="text-ndor-ink">Rationale preservation.</span>{' '}
                Reasoning trails are attached to advances and rejections
                — not just the outcome.
              </li>
              <li>
                <span className="text-ndor-ink">Defensibility.</span>{' '}
                When someone asks how a hire was made, the evidence trail
                is intact.
              </li>
              <li>
                <span className="text-ndor-ink">Auditability.</span>{' '}
                Decision packs can be exported and reviewed by anyone with
                appropriate access.
              </li>
            </ul>
            <p className="text-[11px] italic text-ndor-ink-muted pt-2 border-t border-ndor-edge/50">
              Roles &amp; permissions inside your organisation are managed
              on the{' '}
              <Link
                to="/employer/help/permissions"
                className="underline"
              >
                Permissions
              </Link>{' '}
              page.
            </p>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
